'use client';

import { createContext, ReactNode, useContext, useEffect } from 'react';

import { usePayment } from '@context/paymentContext/PaymentProvider';
import { isServer } from '@utils/isServer';
import { DEFAULT_THEME, ImageName } from '@constants/themes';
import { ThemeNames } from '@interfaces/themes';
import IMAGES_SETS from '@themes/ImagesSets';
import { StaticImageData } from '@components/OptimizedImage';

interface ThemeContextType {
  theme: ThemeNames;
  image: Record<ImageName, string | StaticImageData>;
}

export const ThemeContext = createContext<ThemeContextType>({
  theme: ThemeNames.default,
  image: IMAGES_SETS[ThemeNames.default],
});

export function useTheme() {
  return useContext(ThemeContext);
}

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeMap = {
  '1': ThemeNames.default,
  '2': ThemeNames.voltex,
  '3': ThemeNames.rio,
};

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { order } = usePayment();
  const theme =
    ThemeMap[order.frontend_data?.design_id as keyof typeof ThemeMap] ??
    DEFAULT_THEME;
  const documentElement = isServer ? null : document?.documentElement;
  const image = IMAGES_SETS[theme];

  useEffect(() => {
    documentElement?.setAttribute('data-theme', theme);
  }, [documentElement, theme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        image,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
